import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function JobListDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('accessToken');

      // Fetch all stats in parallel
      const [clientsRes, invoicesRes, jobsRes, pipelineRes] = await Promise.all([
        fetch(`${API_URL}/clients/stats/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/invoices/stats/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/jobs/stats/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/pipeline/view`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!clientsRes.ok || !invoicesRes.ok || !jobsRes.ok || !pipelineRes.ok) {
        throw new Error('Failed to fetch dashboard stats');
      }

      const [clientsData, invoicesData, jobsData, pipelineData] = await Promise.all([
        clientsRes.json(),
        invoicesRes.json(),
        jobsRes.json(),
        pipelineRes.json(),
      ]);

      setStats({
        clients: {
          total: clientsData.total_clients || 0,
          active: clientsData.active_clients || 0,
        },
        invoices: {
          total: invoicesData.total_invoices || 0,
          outstanding: invoicesData.total_outstanding || 0,
          overdue: invoicesData.overdue_count || 0,
          pending_approval: invoicesData.pending_approval_count || 0,
        },
        jobs: {
          total: jobsData.total_jobs || 0,
          total_revenue: jobsData.total_revenue || 0,
          total_profit: jobsData.total_profit || 0,
        },
        pipeline: {
          total_jobs: pipelineData.stats?.total_jobs || 0,
          total_value: pipelineData.stats?.total_value || 0,
        },
      });
    } catch (err) {
      console.error('Failed to load dashboard stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Job List & Invoicing Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Welcome back to your dashboard
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}
        {/* Primary Metrics Cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {/* Clients Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Clients</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats?.clients.active ?? '--'}
                </p>
                {stats && (
                  <p className="text-xs text-gray-500 mt-1">
                    of {stats.clients.total} total
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Invoices Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Invoices</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats?.invoices.total ?? '--'}
                </p>
                {stats && stats.invoices.overdue > 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    {stats.invoices.overdue} overdue
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Jobs Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Jobs</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats?.jobs.total ?? '--'}
                </p>
                {stats && (
                  <p className="text-xs text-gray-500 mt-1">
                    £{stats.jobs.total_revenue.toLocaleString()} revenue
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Pipeline Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pipeline Value</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats ? `£${stats.pipeline.total_value.toLocaleString()}` : '--'}
                </p>
                {stats && (
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.pipeline.total_jobs} opportunities
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Metrics Cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-8">
          {/* Outstanding Invoices Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Outstanding</p>
                <p className="text-2xl font-semibold text-orange-600">
                  {stats ? `£${stats.invoices.outstanding.toLocaleString()}` : '--'}
                </p>
                <p className="text-xs text-gray-500 mt-1">Unpaid invoices</p>
              </div>
              <div className="flex-shrink-0 bg-orange-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total Profit Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Profit</p>
                <p className="text-2xl font-semibold text-emerald-600">
                  {stats ? `£${stats.jobs.total_profit.toLocaleString()}` : '--'}
                </p>
                <p className="text-xs text-gray-500 mt-1">From all jobs</p>
              </div>
              <div className="flex-shrink-0 bg-emerald-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Pending Approvals Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending Approval</p>
                <p className="text-2xl font-semibold text-amber-600">
                  {stats?.invoices.pending_approval ?? '--'}
                </p>
                <p className="text-xs text-gray-500 mt-1">Invoices awaiting approval</p>
              </div>
              <div className="flex-shrink-0 bg-amber-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions & Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <div className="bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-gray-900">Quick Actions</h3>
                <div className="mt-3 space-y-2">
                  <button onClick={() => navigate('/admin/joblist/jobs/create')} className="block text-sm text-emerald-700 hover:text-emerald-900 text-left">
                    → Create New Job/Quote
                  </button>
                  <button onClick={() => navigate('/admin/joblist/invoices/create')} className="block text-sm text-emerald-700 hover:text-emerald-900 text-left">
                    → Create New Invoice
                  </button>
                  <button onClick={() => navigate('/admin/joblist/clients')} className="block text-sm text-emerald-700 hover:text-emerald-900 text-left">
                    → Manage Clients
                  </button>
                  <button onClick={() => navigate('/admin/joblist/pipeline')} className="block text-sm text-emerald-700 hover:text-emerald-900 text-left">
                    → View Sales Pipeline
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Coming Soon Features */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-blue-900">Coming Soon</h3>
                <div className="mt-2 text-sm text-blue-800">
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Xero Integration with sync workflows</li>
                    <li>Advanced reporting and analytics</li>
                    <li>Revenue charts and forecasting</li>
                    <li>Email notifications</li>
                    <li>Export to Excel functionality</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
}

